<script setup lang="ts">
  import { getCurrentInstance } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import type { IReservation } from '@/interfaces/IReservation';
  import V2ReservationTagsEditor from './V2ReservationTagsEditor.vue';
  defineProps<{
    reservation: IReservation;
    notes: string;
    tags: string[];
    dirty: boolean;
    busy: boolean;
    disabled: boolean;
    error?: boolean;
  }>();
  const emit = defineEmits<{
    (event: 'notes', value: string): void;
    (event: 'tags', value: string[]): void;
    (event: 'save'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  function occasion(value: string) {
    return proxy.$te(`reservations.occasion.${value}`)
      ? String(proxy.$t(`reservations.occasion.${value}`))
      : value;
  }
</script>
<template>
  <div class="reservation-notes-editor">
    <section class="detail-section">
      <template v-if="reservation.notesGuest"
        ><h3>{{ $t('reservations.detail.guestNotes') }}</h3>
        <p class="section-hint">
          {{ $t('reservations.detail.guestNotesHint') }}
        </p>
        <p class="guest-request">{{ reservation.notesGuest }}</p></template
      >
      <div v-if="reservation.allergies" class="allergy-note">
        <strong>{{ $t('reservations.detail.allergies') }}</strong
        ><span>{{ reservation.allergies }}</span>
      </div>
      <label class="notes-label" for="reservation-detail-notes">{{
        $t('reservations.detail.internalNotes')
      }}</label>
      <p class="section-hint">
        {{ $t('reservations.detail.internalNotesHint') }}
      </p>
      <PosUiTextarea
        id="reservation-detail-notes"
        :value="notes"
        :rows="4"
        :maxlength="10000"
        :disabled="disabled"
        :placeholder="$t('reservations.detail.internalNotesPlaceholder')"
        @input="emit('notes', $event)"
        @blur.native="emit('save')"
      />
      <p v-show="error" class="save-error" role="alert">
        {{ $t('reservations.detail.saveError') }}
      </p>
      <PosUiButton
        v-show="dirty || error"
        color="neutral"
        variant="ghost"
        :disabled="busy"
        @click="emit('save')"
        >{{ $t(error ? 'reservations.page.retry' : 'save') }}</PosUiButton
      >
    </section>
    <section class="detail-section">
      <div v-if="reservation.occasion" class="occasion-row">
        <h3>{{ $t('reservations.detail.occasion') }}</h3>
        <PosUiBadge color="neutral" variant="soft">{{
          occasion(reservation.occasion)
        }}</PosUiBadge>
      </div>
      <h3 class="tags-label">{{ $t('reservations.detail.tags') }}</h3>
      <V2ReservationTagsEditor
        :value="tags"
        :disabled="disabled"
        @input="emit('tags', $event)"
      />
    </section>
  </div>
</template>
<style scoped lang="scss">
  .reservation-notes-editor {
    .detail-section {
      @apply tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;
      h3 {
        @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }
    }
    .section-hint {
      @apply tw-mt-1 tw-text-xs tw-text-neutral-400;
    }
    .muted {
      @apply tw-text-sm tw-text-neutral-500;
    }
    .save-error {
      @apply tw-mt-2 tw-text-xs tw-text-red-700;
    }
    .guest-request {
      @apply tw-mb-4 tw-mt-2 tw-whitespace-pre-wrap tw-rounded-lg tw-bg-neutral-100 tw-p-3 tw-text-sm tw-text-neutral-700;
    }
    .allergy-note {
      @apply tw-mb-4 tw-flex tw-flex-col tw-gap-1 tw-rounded-lg tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-900;
    }
    .notes-label {
      @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }
    :deep(.pos-ui-textarea) {
      @apply tw-mt-2;
    }
    .occasion-row {
      @apply tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-2;
    }
    .tags-label {
      @apply tw-mb-3;
    }
  }
</style>
